import { Box, Link, Stack } from '@mui/material';
import { FC } from 'react';

import {
  AbgButton,
  AbgTypography,
  Loader,
  mergeSx,
  useTranslation,
} from '@abg/kit-base';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { useClearSession } from '../../../../hooks/use-clear-session';
import {
  bookingWidgetFormStoreSelector,
} from '../../../../store';
import AccountTodayRental from '../../../AccountTodayRental';
import { AccountMenuProps } from '../../account-menu.types';
import InfoStepper from '../InfoStepper/InfoStepper';

import {
  itemsContainerStyling,
  logoutButtonStyling,
  titleStyling,
} from './account-actions.styling';

export const AccountActions: FC<AccountMenuProps> = ({
  model,
  handleClose,
  styleParts = {},
  currentRental,
}) => {
  const {
    i18n,
    accountMenuTitle,
    accountMenuItems,
    showNudge,
    accountActivationStepsNumber,
  } = model;
  const t = useTranslation(i18n);
  const { navigationLinks, brand } = usePageContext();

  const { clearSession, isLoading } = useClearSession();
  const resetUserDataToDefault =
    bookingWidgetFormStoreSelector.use.resetUserDataToDefault?.();

  const handleLogout = async (): Promise<void> => {
    await clearSession();
    resetUserDataToDefault?.();

    handleClose();

    // setting href because router.replace sometimes doesn't work in this case
    globalThis.location.href = navigationLinks.home;
  };

  return (
    <Stack gap={2}>
      {isLoading && <Loader isActive brand={brand} />}
      {accountMenuTitle && (
        <AbgTypography sx={titleStyling} data-testid="account-menu-title">
          {accountMenuTitle}
        </AbgTypography>
      )}
      <AccountTodayRental i18n={i18n} currentRental={currentRental} />
      <Box sx={mergeSx(itemsContainerStyling, styleParts.menuItems)}>
        {accountMenuItems?.map((link, index) => (
          <Link
            href={link.url}
            target={link.target}
            key={link.title}
            data-testid={`account-menu-link-${index}`}
          >
            {link.title}
          </Link>
        ))}
        <InfoStepper
          i18n={i18n}
          totalSteps={accountActivationStepsNumber}
          showNudge={showNudge}
        />
      </Box>
      <AbgButton
        variant="contained"
        className="primary"
        sx={mergeSx(logoutButtonStyling, styleParts.logoutButton)}
        data-testid="account-menu-logout-btn"
        onClick={() => handleLogout()}
      >
        {t('global.logout.label')}
      </AbgButton>
    </Stack>
  );
};
